import { AssetKind, EpisodeStatus } from './catalog';

/**
 * - `discover` ("By category"): walk a listing's pages and index the movies on them.
 * - `walk` ("Random walk"): from one page, follow links between movie pages in random
 *   order until none is left unvisited or a page limit is reached.
 * - `refresh`: fetch old movie pages again, and check old video streams again.
 * - `categories`: read a source's category indexes; the service starts these.
 * - `episodes` and `assets`: earlier kinds, kept so their jobs still show. Refresh
 *   took over their work, and neither can be started any more.
 *
 * `discover` and `walk` only create: a movie page already indexed is never fetched
 * again by them. Bringing indexed content up to date is `refresh`'s job alone.
 */
export const JOB_KINDS = ['discover', 'walk', 'refresh', 'categories', 'episodes', 'assets'] as const;

export type JobKind = (typeof JOB_KINDS)[number];

export const JOB_STATUSES = ['pending', 'running', 'paused', 'completed', 'failed', 'cancelled'] as const;

export type JobStatus = (typeof JOB_STATUSES)[number];

/** `walk_step` is one page a random walk visits: a movie page, or the page it starts from. */
export const CRAWL_ITEM_KINDS = ['list_page', 'movie_page', 'episode_check', 'asset', 'category_index', 'walk_step'] as const;

export type CrawlItemKind = (typeof CRAWL_ITEM_KINDS)[number];

export const CRAWL_ITEM_STATUSES = ['pending', 'claimed', 'done', 'failed', 'dead', 'cancelled'] as const;

export type CrawlItemStatus = (typeof CRAWL_ITEM_STATUSES)[number];

export interface DiscoverParams {
  /** Listings to walk, by the source's name for them: `new`, `censored`, `genres/creampie`. Defaults to `new`. */
  readonly lists?: ReadonlyArray<string>;
  /** The page each listing starts at. Defaults to 1. */
  readonly firstPage?: number;
  /** The last page of each listing to read, by page number: with `firstPage`, a range. */
  readonly maxPages?: number;
  /** Keep walking past pages whose movies are all known already. Defaults to true with `firstPage`, else false. */
  readonly full?: boolean;
}

export interface WalkParams {
  /** The page to start from; the source's home page when absent. */
  readonly startUrl?: string;
  /** Stop once this many movie pages have been fetched. Walking through known pages fetches nothing and does not count. */
  readonly maxPages?: number;
}

/** At least one of the two. A threshold of 0 takes everything. */
export interface RefreshParams {
  /** Fetch again every page last fetched at least this many days ago. */
  readonly pagesOlderThanDays?: number;
  /** Check again every video stream last checked at least this many days ago, and every one that was not playing. */
  readonly streamsOlderThanDays?: number;
}

export interface EpisodesParams {
  /** Which last results to try again. Defaults to everything but `ok`. */
  readonly statuses?: ReadonlyArray<string>;
}

/** An assets job takes no parameters. */
export type AssetsParams = Readonly<Record<string, never>>;

export type JobParams = DiscoverParams | WalkParams | RefreshParams | EpisodesParams | AssetsParams;

/** How many of a job's crawl items are in each state. */
export type JobProgress = Readonly<Record<CrawlItemStatus, number>>;

export interface Job {
  readonly jobId: number;
  readonly kind: JobKind;
  readonly sourceId?: string;
  readonly params: Readonly<Record<string, unknown>>;
  readonly status: JobStatus;
  readonly createdAt: number;
  readonly startedAt?: number;
  readonly finishedAt?: number;
  readonly error?: string;
  readonly progress: JobProgress;
}

/** A crawl item that ran out of retries, for the jobs UI. */
export interface DeadCrawlItem {
  readonly itemId: number;
  readonly kind: CrawlItemKind;
  readonly target: string;
  readonly attempts: number;
  readonly lastError?: string;
}

export const ATTEMPT_OUTCOMES = ['succeeded', 'failed', 'gave_up', 'interrupted'] as const;

export type AttemptOutcome = (typeof ATTEMPT_OUTCOMES)[number];

/** One run of one crawl item. */
export interface CrawlAttempt {
  readonly attemptId: number;
  readonly itemId: number;
  readonly kind: CrawlItemKind;
  readonly target: string;
  readonly jobId?: number;
  readonly startedAt: number;
  readonly finishedAt: number;
  readonly outcome: AttemptOutcome;
  readonly httpStatus?: number;
  readonly error?: string;
  /** URLs the run queued that the queue had not seen, and ones it already knew. */
  readonly queuedNew: number;
  readonly queuedKnown: number;
  readonly subject?: CrawlItemSubject;
}

/** Runs finished in one bucket of a job's timeline. */
export interface JobTimelinePoint {
  readonly at: number;
  readonly succeeded: number;
  readonly failed: number;
}

export type AttemptTotals = Readonly<Record<AttemptOutcome, number>>;

/** A crawl item's current state, as one URL's page shows it. */
export interface CrawlItemDetail {
  readonly itemId: number;
  readonly sourceId: string;
  readonly kind: CrawlItemKind;
  readonly target: string;
  readonly status: CrawlItemStatus;
  readonly jobId?: number;
  readonly attempts: number;
  readonly createdAt: number;
  readonly startedAt?: number;
  readonly lastFinishedAt?: number;
  readonly lastSucceededAt?: number;
  readonly nextAttemptAt?: number;
  readonly lastError?: string;
  readonly parent?: { readonly itemId: number; readonly kind: CrawlItemKind; readonly target: string; readonly subject?: CrawlItemSubject };
  readonly subject?: CrawlItemSubject;
}

export interface CrawlItemChild {
  readonly itemId: number;
  readonly kind: CrawlItemKind;
  readonly target: string;
  readonly status: CrawlItemStatus;
  readonly subject?: CrawlItemSubject;
}

/**
 * What an item's target names in the catalogue, so `asset:331` can read as the cover of
 * FC2-PPV-4991108 and `episode:98` as its first episode. Absent for a URL the catalogue
 * has not saved yet, and for list pages, whose URL says it all.
 */
export interface CrawlItemSubject {
  /** The movie the item belongs to. */
  readonly code?: string;
  readonly episodeNo?: number;
  /** Where the episode plays, such as `sadie-shop.site`. */
  readonly playerHost?: string;
  readonly episodeStatus?: EpisodeStatus;
  /** Heights the episode was found in, such as `1280x720`. */
  readonly resolutions?: ReadonlyArray<string>;
  readonly durationS?: number;
  readonly assetKind?: AssetKind;
  /** Which of several files of one kind: the n-th scene sprite. */
  readonly assetSeq?: number;
  readonly bytes?: number;
  /** Whose photo an asset is. */
  readonly personName?: string;
}

/** One of a job's crawl items, as its page lists them. */
export interface JobItem {
  readonly itemId: number;
  readonly kind: CrawlItemKind;
  readonly target: string;
  readonly status: CrawlItemStatus;
  /** Tries in the current round; a later crawl of the same URL starts again from 0. */
  readonly attempts: number;
  readonly createdAt: number;
  readonly lastFinishedAt?: number;
  /** When a failed item is tried again. */
  readonly nextAttemptAt?: number;
  readonly lastError?: string;
  readonly subject?: CrawlItemSubject;
}
